<template>
  <div class="chat-wrapper">
    <div class="chat-window">
      <div
        v-for="item in messages"
        :key="item.id"
        class="message"
        :class="item.role"
      >
        <div class="avatar">{{ item.role === 'assistant' ? 'LLM' : '我' }}</div>
        <div class="bubble">
          <div v-if="item.type === 'text'">
            <div
              v-if="item.role === 'assistant'"
              class="markdown"
              v-html="renderMarkdown(item.content)"
            ></div>
            <p v-else>{{ item.content }}</p>
          </div>
          <img
            v-else
            :src="item.content"
            alt="uploaded"
            class="chat-image"
            @click="openImagePreview(item.content)"
          />
        </div>
      </div>
    </div>

    <div class="input-area">
      <div class="input-row">
        <textarea
          v-model="inputText"
          class="question-input"
          rows="2"
          :placeholder="chatType === 'imageDetection' ? 'Type a label and press Enter' : 'Please enter your question'"
          @keydown.enter.prevent="chatType === 'imageDetection' && onDetectionEnter()"
        />
        <div
          v-if="chatType === 'imageDetection' && detectionTags.length"
          class="tags-row"
        >
          <span
            v-for="(tag, idx) in detectionTags"
            :key="`${tag}-${idx}`"
            class="tag-chip"
          >
            {{ tag }}
            <button class="tag-remove" @click="removeTag(idx)">×</button>
          </span>
        </div>
        <div class="actions">
          <button
            class="send-btn"
            :disabled="sending"
            @click="onSend"
          >
            {{ sending ? 'Sending...' : 'Send' }}
          </button>
          <button
            class="get-bj-pics-btn"
            :disabled="chatType === 'textChat'"
            @click="openBJPicsModal"
          >
            Get BJ Pics
          </button>
          <button
            class="upload-btn"
            :disabled="chatType === 'textChat'"
            @click="openFilePicker"
          >
            Upload Image
          </button>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="file-input"
            @change="onFileChange"
          />
        </div>
      </div>
      <div class="select-row">
        <select v-model="chatType" class="chat-type-select">
          <option value="textChat">textChat</option>
          <option value="imageDescription">imageDescription</option>
          <option value="imageDetection">imageDetection</option>
        </select>
      </div>
    </div>

    <!-- Image Preview -->
    <div
      v-if="showImagePreview"
      class="image-preview-overlay"
      @click="closeImagePreview"
    >
      <div class="image-preview-content" @click.stop>
        <button class="close-btn" @click="closeImagePreview">×</button>
        <img :src="previewSrc" alt="preview" />
      </div>
    </div>

    <!-- BJ Pics Modal -->
    <div v-if="showBJPicsModal" class="modal-overlay" @click="closeBJPicsModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Select Images</h3>
          <button class="close-btn" @click="closeBJPicsModal">×</button>
        </div>
        <div class="modal-body">
          <div v-if="loadingBJPics" class="loading">Loading...</div>
          <div v-else-if="bjPicsList.length === 0" class="empty">No images found</div>
          <div v-else class="image-grid">
            <div
              v-for="(item, index) in bjPicsList"
              :key="index"
              class="image-item"
              :class="{ selected: selectedBJPics.includes(item.picUrl) }"
              @click="toggleBJPic(item.picUrl)"
            >
              <img
                :src="`https://bj-static.aimo.tech/${item.picUrl}`"
                :alt="`Image ${index + 1}`"
              />
              <div v-if="selectedBJPics.includes(item.picUrl)" class="selected-overlay">
                ✓
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="confirm-btn" @click="confirmBJPicsSelection">Confirm</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { buildApiUrl, API_TOKEN } from '@/config/api';

export default Vue.extend({
  name: 'ChatTrainLLMPrompt',
  data() {
    return {
      inputText: '',
      messages: [],
      pendingImageUrl: null, // 只保存最后一张图片的picUrl
      pendingImageContent: null, // 本地上传图片的 base64
      sending: false,
      chatType: 'textChat',
      showBJPicsModal: false,
      bjPicsList: [],
      selectedBJPics: [],
      loadingBJPics: false,
      userId: 2131,
      detectionTags: [],
      showImagePreview: false,
      previewSrc: '',
    };
  },
  methods: {
    genId() {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    },
    renderMarkdown(text) {
      if (!text) return '';
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br/>');
    },
    async onSend() {
      const isDetection = this.chatType === 'imageDetection';
      const question = this.inputText.trim();

      // imageDetection: Enter 会转成标签，但如果还有输入未回车，先转成标签
      if (isDetection && question) {
        this.addDetectionTag(question);
        this.inputText = '';
      }

      const hasQuestion = question.length > 0;
      const hasTags = this.detectionTags.length > 0;

      // 校验：text/imageDescription 需要文字；imageDetection 需要至少1个标签
      if (!isDetection && !hasQuestion) return;
      if (isDetection && !hasTags) return;

      const promptToSend = isDetection ? this.detectionTags.join(',') : question;

      // 添加用户消息到聊天框，展示当前 prompt
      this.messages.push({
        id: this.genId(),
        role: 'user',
        type: 'text',
        content: promptToSend,
      });

      const imgUrlToSend = this.pendingImageUrl; // 只使用最后一张图片
      const imageContentToSend = this.pendingImageContent; // 本地上传的 base64
      this.inputText = '';

      try {
        this.sending = true;
        const answer = await this.callLLM(promptToSend, imgUrlToSend, imageContentToSend);

        if (this.chatType === 'imageDetection' && answer && answer.data) {
          this.messages.push({
            id: this.genId(),
            role: 'assistant',
            type: 'image',
            content: answer.data,
          });
        } else {
          const text = answer && (answer.message || answer.data || '请求成功');
          this.messages.push({
            id: this.genId(),
            role: 'assistant',
            type: 'text',
            content: text,
          });
        }
      } catch (error) {
        console.error('发送失败', error);
      } finally {
        this.sending = false;
        if (isDetection) {
          this.detectionTags = [];
        }
      }
    },
    openFilePicker() {
      const input = this.$refs.fileInput;
      if (input) input.click();
    },
    async onFileChange(event) {
      const input = event.target;
      const file = input && input.files && input.files[0];
      if (!file) return;

      try {
        // 将本地图片转为 base64
        const base64 = await this.fileToBase64(file);

        // 本地上传：只保存 base64，imgUrl 置空
        this.pendingImageUrl = null;
        this.pendingImageContent = base64;

        // 显示图片到聊天框（使用 base64 显示）
        this.messages.push({
          id: this.genId(),
          role: 'user',
          type: 'image',
          content: base64,
        });
      } catch (error) {
        console.error('上传失败', error);
      } finally {
        if (input) input.value = '';
      }
    },
    async callLLM(promptMessage, imgUrl, imageContent) {
      try {
        const requestBody = {
          userId: this.userId,
          promptMessage,
          type: this.chatType,
        };
        
        // 如果有图片，添加 imgUrl（只使用最后一张）
        if (imgUrl) {
          requestBody.imgUrl = imgUrl;
        }
        // 本地上传的 base64 图片
        if (imageContent) {
          requestBody.imageContent = imageContent;
        }

        const response = await fetch(buildApiUrl('/trainLLM/trainLLMPrompt'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-Token': API_TOKEN, // 按后端要求使用大写 T
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || `HTTP ${response.status}`);
        }

        const result = await response.json();
        
        if (result.code === 0) {
          // 返回完整结果，便于上层区分文本/图片
          return result;
        } else {
          throw new Error(result.message || '请求失败');
        }
      } catch (error) {
        console.error('调用LLM失败:', error);
        throw error;
      }
    },
    async uploadImage(file) {
      // 示例上传，按需替换为真实上传接口
      await new Promise((resolve) => setTimeout(resolve, 300));
      return URL.createObjectURL(file);
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });
    },
    async openBJPicsModal() {
      this.showBJPicsModal = true;
      this.selectedBJPics = [];
      if (this.bjPicsList.length === 0) {
        await this.fetchBJPics();
      }
    },
    closeBJPicsModal() {
      this.showBJPicsModal = false;
    },
    async fetchBJPics() {
      try {
        this.loadingBJPics = true;
        const response = await fetch(buildApiUrl('/trainLLM/getAllClockOutList'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': API_TOKEN,
          },
        });
        const result = await response.json();
        if (result.code === 0 && result.data) {
          this.bjPicsList = result.data.filter(item => item.picUrl);
        } else {
          console.error('Failed to fetch BJ pics:', result);
          this.bjPicsList = [];
        }
      } catch (error) {
        console.error('Error fetching BJ pics:', error);
        this.bjPicsList = [];
      } finally {
        this.loadingBJPics = false;
      }
    },
    toggleBJPic(picUrl) {
      const index = this.selectedBJPics.indexOf(picUrl);
      if (index > -1) {
        this.selectedBJPics.splice(index, 1);
      } else {
        this.selectedBJPics.push(picUrl);
      }
    },
    confirmBJPicsSelection() {
      if (this.selectedBJPics.length === 0) {
        this.closeBJPicsModal();
        return;
      }
      
      // 只保存最后一张图片的picUrl
      const lastPicUrl = this.selectedBJPics[this.selectedBJPics.length - 1];
      this.pendingImageUrl = lastPicUrl;
      
      // 显示所有选中的图片到聊天框（使用完整URL显示）
      const imagePrefix = 'https://bj-static.aimo.tech/';
      this.selectedBJPics.forEach(picUrl => {
        const fullUrl = imagePrefix + picUrl;
        this.messages.push({
          id: this.genId(),
          role: 'user',
          type: 'image',
          content: fullUrl,
          picUrl: picUrl, // 保存picUrl用于后续发送
        });
      });
      
      this.selectedBJPics = [];
      this.closeBJPicsModal();
    },
    onDetectionEnter() {
      const value = this.inputText.trim();
      if (!value) return;
      this.addDetectionTag(value);
      this.inputText = '';
    },
    addDetectionTag(value) {
      const tag = value.trim();
      if (!tag) return;
      if (!this.detectionTags.includes(tag)) {
        this.detectionTags.push(tag);
      }
    },
    removeTag(idx) {
      this.detectionTags.splice(idx, 1);
    },
    openImagePreview(src) {
      if (!src) return;
      this.previewSrc = src;
      this.showImagePreview = true;
    },
    closeImagePreview() {
      this.showImagePreview = false;
      this.previewSrc = '';
    },
  },
});
</script>

<style scoped>
.chat-wrapper {
  width: 680px;
  margin: 0 auto;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  background: #fafafa;
}

.chat-window {
  height: 460px;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;
  border-bottom: 1px solid #e5e5e5;
}

.message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.message.user {
  flex-direction: row-reverse;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 12px;
}

.message.user .avatar {
  background: #67c23a;
}

.bubble {
  max-width: 70%;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f2f2f2;
  text-align: left;
  word-break: break-word;
}

.message.user .bubble {
  background: #ecf9e6;
}

.bubble img {
  max-width: 240px;
  border-radius: 8px;
  cursor: zoom-in;
}

.input-area {
  padding: 16px;
}

.input-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #ecf5ff;
  color: #409eff;
  border: 1px solid #d3e7ff;
  border-radius: 12px;
  font-size: 12px;
}

.tag-remove {
  background: none;
  border: none;
  color: #409eff;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.select-row {
  display: flex;
  justify-content: flex-end;
}

.chat-type-select {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;
}

.question-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  resize: none;
  font-size: 14px;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.send-btn,
.upload-btn,
.get-bj-pics-btn {
  min-width: 90px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #fff;
  background: #409eff;
  transition: opacity 0.2s ease;
}

.get-bj-pics-btn {
  background: #e6a23c;
}

.upload-btn {
  background: #67c23a;
}

.send-btn:disabled,
.upload-btn:disabled,
.get-bj-pics-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.file-input {
  display: none;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #f0f0f0;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.loading,
.empty {
  text-align: center;
  padding: 40px;
  color: #999;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.image-item {
  position: relative;
  aspect-ratio: 1;
  border: 2px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
}

.image-item:hover {
  border-color: #409eff;
  transform: scale(1.02);
}

.image-item.selected {
  border-color: #409eff;
  border-width: 3px;
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.selected-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(64, 158, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
  font-weight: bold;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
}

.confirm-btn {
  padding: 10px 24px;
  background: #409eff;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.confirm-btn:hover {
  background: #66b1ff;
}

.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.image-preview-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: #000;
  padding: 12px;
  border-radius: 12px;
  display: flex;
  justify-content: center;
}

.image-preview-content img {
  max-width: 85vw;
  max-height: 85vh;
  object-fit: contain;
}
</style>